/**
 * Bucle de timbrado local fuera del ciclo de vida de React.
 *
 * Antes `useCallRingbackSound` usaba `setTimeout` en un `useEffect`: cualquier re-render o
 * `playLocalRingback` en falso un instante limpiaba el timeout y el bucle moría tras el primer bloque.
 * Aquí nos suscribimos al store singleton y encadenamos `setTimeout` leyendo siempre el estado actual.
 *
 * **Perú (`pe_latam`):** el trío “tu-tu-tu” solo en el **primer** tick de cada sesión de timbrado;
 * los ticks siguientes son solo timbre largo + pausa.
 */

import { primeDtmfAudio } from '@/lib/dtmfKeypad';
import {
  hasActiveRemoteAudio,
  startEarlyMediaMonitor,
  stopEarlyMediaMonitor,
} from '@/lib/earlyMediaDetector';
import { getWebRTCState, subscribe } from '@/lib/telnyxWebRTCStore';
import {
  getPeLatamFirstCycleMs,
  getPeLatamLongCycleMs,
  getRingbackMode,
  getRingbackPreset,
  getUsCompactCycleMs,
  getUseSyntheticRingback,
  isEarlyMediaAutoMode,
  playPeLatamIntroPlusLong,
  playPeLatamLongPulse,
  playUsCompactPulse,
} from '@/lib/ringbackAudio';
import { devLog } from '@/lib/utils';

let timeoutId: ReturnType<typeof setTimeout> | null = null;
let storeListenerRegistered = false;
/** Tras el primer ciclo `pe_latam` con trío, los siguientes son solo largo. */
let peLatamIntroAlreadyPlayed = false;

function resetPeLatamSession(): void {
  peLatamIntroAlreadyPlayed = false;
}

function stopLoop(): void {
  if (timeoutId != null) {
    clearTimeout(timeoutId);
    timeoutId = null;
  }
}

/** Para completamente timbrado + monitor (fin de llamada). */
function stopAll(): void {
  stopLoop();
  stopEarlyMediaMonitor();
}

/** ¿El store indica que deberíamos estar en fase de timbrado (independiente de early media)? */
function isInRingbackPhase(): boolean {
  if (!getUseSyntheticRingback()) return false;
  const s = getWebRTCState();
  return s.playLocalRingback && (s.status === 'connected' || s.status === 'connecting');
}

function tick(): void {
  if (!isInRingbackPhase()) {
    stopAll();
    resetPeLatamSession();
    return;
  }
  /** En modo auto: early media detectado → pausar tonos sintéticos pero mantener monitor. */
  if (isEarlyMediaAutoMode() && hasActiveRemoteAudio()) {
    timeoutId = setTimeout(tick, 300);
    return;
  }

  /** Tras un gesto previo en la app, asegura `AudioContext.resume()` antes de cada pulso (auto-llamada, pestaña vuelta a foco, etc.). */
  primeDtmfAudio();

  const preset = getRingbackPreset();

  if (preset === 'us_compact') {
    playUsCompactPulse();
    timeoutId = setTimeout(tick, getUsCompactCycleMs());
    return;
  }

  const autoMode = isEarlyMediaAutoMode();

  if (!peLatamIntroAlreadyPlayed) {
    /** Modo auto: solo los 3 puntitos; el WAV server-side se encarga del "tuuu". */
    playPeLatamIntroPlusLong(autoMode);
    peLatamIntroAlreadyPlayed = true;
    timeoutId = setTimeout(tick, autoMode ? 1000 : getPeLatamFirstCycleMs());
  } else if (!autoMode) {
    /** Solo en modo forzado: reproducir "tuuu" sintético. En auto el WAV ya está sonando. */
    playPeLatamLongPulse();
    timeoutId = setTimeout(tick, getPeLatamLongCycleMs());
  } else {
    /** Modo auto tras intro: solo seguir monitoreando early media, sin reproducir nada. */
    timeoutId = setTimeout(tick, 500);
  }
}

function startLoop(): void {
  stopLoop();
  if (!isInRingbackPhase()) return;
  resetPeLatamSession();
  const mode = getRingbackMode();
  devLog.log('[ringbackLoop] startLoop — mode:', mode, '| earlyMediaAuto:', mode === 'auto');
  if (mode === 'auto') {
    startEarlyMediaMonitor();
  }
  tick();
}

function onStoreChange(): void {
  if (isInRingbackPhase()) {
    if (timeoutId === null) {
      startLoop();
    }
  } else {
    stopAll();
    resetPeLatamSession();
  }
}

/**
 * Registrar una sola vez (p. ej. al montar CallCenterProvider).
 * Idempotente en React Strict Mode / HMR.
 */
export function ensureCallRingbackLoop(): void {
  if (storeListenerRegistered) {
    onStoreChange();
    return;
  }
  storeListenerRegistered = true;
  subscribe(() => {
    onStoreChange();
  });
  onStoreChange();
}

/** Tras cambiar preset en Ajustes: reinicia intervalos si el timbrado está sonando. */
export function restartRingbackLoopIfPlaying(): void {
  if (!isInRingbackPhase()) return;
  startLoop();
}
